import axios, { type AxiosInstance } from "axios";
import { config } from "../config";
import type { IWidgetSession } from "../shared/session/interfaces/session";
import { LINK_ERROR_CODE } from "../utils/commons";
import { useSessionStore } from "../store/useSessionStore";

/**
 * Error class for link claim errors
 */
export class LinkClaimError extends Error {
  constructor(
    message: string,
    public code: LINK_ERROR_CODE,
    public statusCode?: number
  ) {
    super(message);
    this.name = "LinkClaimError";
  }
}

/**
 * Custom event for session expired (LINK_NOT_FOUND or LINK_EXPIRED)
 * This event is dispatched when the API returns these errors
 * on any endpoint (except the initial claim/session endpoints which handle errors differently)
 */
export const SESSION_EXPIRED_EVENT = "m2m-session-expired";

/**
 * Extract HTTP status and backend error payload from an unknown thrown value.
 * Returns undefined fields when the error is not an Axios error.
 */
const getApiErrorInfo = (
  error: unknown
): {
  status?: number;
  code?: LINK_ERROR_CODE;
  message?: string;
} => {
  if (!axios.isAxiosError(error)) return {};
  const payload = error.response?.data?.error;
  return {
    status: error.response?.status,
    code: payload?.code,
    message: payload?.message,
  };
};

/**
 * True when the backend has no handler for the route at all (NestJS answers
 * `404 { code: "NOT_FOUND", message: "Cannot POST /path" }`), as opposed to a
 * domain 404 such as LINK_NOT_FOUND. Lets callers degrade gracefully while an
 * endpoint is not deployed yet.
 */
export const isRouteNotFoundError = (error: unknown): boolean => {
  const { status, code, message } = getApiErrorInfo(error);
  return (
    status === 404 &&
    String(code) === "NOT_FOUND" &&
    /^Cannot (GET|POST|PUT|PATCH|DELETE) /.test(message ?? "")
  );
};

/**
 * Current link ID for API requests
 * Set after successful claim via setWidgetLinkId()
 */
let currentLinkId: string | null = null;

/**
 * Set the current link ID for API requests
 * This adds the x-link-id header to all subsequent requests
 * @param linkId - The link ID to use, or null to clear
 */
export const setWidgetLinkId = (linkId: string | null) => {
  currentLinkId = linkId;
};

/**
 * Create axios instance for widget API calls
 */
const createWidgetApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: config.API_BASE_URL,
  });

  // Request interceptor to add x-link-id header
  instance.interceptors.request.use((config) => {
    if (currentLinkId) {
      config.headers["x-link-id"] = currentLinkId;
    } else {
      // Fallback: restore linkId from persisted session store (survives page reloads)
      // This handles cases where currentLinkId is lost (e.g., after KYC/CIP redirect)
      const sessionData = useSessionStore.getState().session.data;
      if (sessionData?.linkId) {
        currentLinkId = sessionData.linkId;
        config.headers["x-link-id"] = currentLinkId;
      }
    }
    return config;
  });

  // Response interceptor to catch LINK_NOT_FOUND and LINK_EXPIRED errors globally
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Check if the error is LINK_NOT_FOUND or LINK_EXPIRED from any endpoint
      const errorCode = error?.response?.data?.error?.code;
      const statusCode = error?.response?.status;
      
      if (
        errorCode === "LINK_NOT_FOUND" ||
        errorCode === "LINK_EXPIRED" ||
        statusCode === 410
      ) {
        // Dispatch custom event for session expired
        window.dispatchEvent(
          new CustomEvent(SESSION_EXPIRED_EVENT, {
            detail: { code: errorCode || "LINK_EXPIRED" },
          })
        );
      }
      
      return Promise.reject(error);
    }
  );

  return instance;
};

// Create singleton instance for widget API
const widgetApi = createWidgetApiInstance();

/**
 * Device info to send with claim request
 */
export interface IDeviceInfo {
  userAgent?: string;
  language?: string;
  timezone?: string;
}

/**
 * Claim a magic link by linkId
 * POST /widget/links/:linkId/claim
 */
export const claimLink = async (
  linkId: string,
  deviceInfo?: IDeviceInfo
): Promise<IWidgetSession> => {
  const apiBaseUrl = config.API_BASE_URL;

  try {
    const { data } = await axios.post(
      `${apiBaseUrl}/widget/links/${linkId}/claim`,
      { deviceInfo }
    );

    // Map backend response to IWidgetSession
    return {
      sessionId: data.data.sessionId,
      linkId: data.data.linkId,
      token: data.data.token,
      status: data.data.status,
      expiresAt: data.data.expiresAt,
      partner: {
        partnerId: data.data.partner.partnerId,
        name: data.data.partner.name,
        logoUrl: data.data.partner.logoUrl,
        primaryColor: data.data.partner.primaryColor,
        theme: data.data.partner.theme,
        supportEmail: data.data.partner.supportEmail,
        supportWhatsappNumber: data.data.partner.supportWhatsappNumber,
        sessionExpiredRedirectUrl: data.data.partner.sessionExpiredRedirectUrl,
      },
      userData: data.data.userData,
      config: data.data.config,
      language: data.data.language,
      flavor: data.data.flavor,
      themeMode: data.data.themeMode,
      termsAccepted: data.data.termsAccepted ?? false,
      termsAcceptedAt: data.data.termsAcceptedAt,
      cipStatus: data.data.cipStatus,
      // Optional — legacy backends omit it (stays undefined).
      hasPhoneNumber:
        typeof data.data.hasPhoneNumber === "boolean"
          ? data.data.hasPhoneNumber
          : undefined,
    };
  } catch (error: unknown) {
    const { status, code, message } = getApiErrorInfo(error);

    // Handle specific error codes from backend
    if (status === 400) {
      throw new LinkClaimError(
        message || "Invalid token",
        code || LINK_ERROR_CODE.INVALID_TOKEN,
        400
      );
    }

    if (status === 404) {
      throw new LinkClaimError(
        "Link not found",
        LINK_ERROR_CODE.LINK_NOT_FOUND,
        404
      );
    }

    if (status === 410) {
      throw new LinkClaimError(
        "Link expired",
        LINK_ERROR_CODE.LINK_EXPIRED,
        410
      );
    }

    if (status === 409) {
      throw new LinkClaimError(
        "Link already used",
        LINK_ERROR_CODE.LINK_ALREADY_USED,
        409
      );
    }

    // Generic error
    throw new LinkClaimError(
      "Failed to claim link",
      LINK_ERROR_CODE.INTERNAL_ERROR,
      status
    );
  }
};

/**
 * Accept terms and conditions for the current user
 * POST /widget/users/me/terms
 */
export const acceptUserTerms = async (): Promise<{ termsAccepted: boolean; termsAcceptedAt?: string }> => {
  const { data } = await widgetApi.post(`/widget/users/me/terms`);
  return data.data;
};

/**
 * Save the user's phone number (post-KYC / tracking notifications)
 * POST /widget/users/me/phone
 */
export const updateUserPhoneNumber = async (
  phoneNumber: string,
  locale?: "es" | "en"
): Promise<{ hasPhoneNumber: boolean }> => {
  const { data } = await widgetApi.post(`/widget/users/me/phone`, {
    phoneNumber,
    ...(locale ? { locale } : {}),
  });
  return { hasPhoneNumber: data?.data?.hasPhoneNumber ?? true };
};

/**
 * First-send warning notice. Text, threshold and on/off are managed from the
 * superadmin portal; the backend returns enabled=false when it is off.
 * GET /widget/links/:linkId/notices/first-send-warning
 */
export interface IFirstSendWarningNotice {
  enabled: boolean;
  isFirstSend?: boolean;
  thresholdAmount?: number;
  currency?: string;
  title?: { es: string; en: string };
  body?: { es: string; en: string };
}

export const getFirstSendWarning = async (
  linkId: string
): Promise<IFirstSendWarningNotice> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/notices/first-send-warning`
  );
  return data.data;
};

/**
 * Get session for a linkId
 * GET /widget/links/:linkId/session
 */
export const getSession = async (linkId: string): Promise<IWidgetSession> => {
  const apiBaseUrl = config.API_BASE_URL;

  try {
    const { data } = await axios.get(
      `${apiBaseUrl}/widget/links/${linkId}/session`
    );

    return {
      sessionId: data.data.sessionId,
      linkId: data.data.linkId,
      token: data.data.token,
      status: data.data.status,
      expiresAt: data.data.expiresAt,
      partner: {
        partnerId: data.data.partner.partnerId,
        name: data.data.partner.name,
        logoUrl: data.data.partner.logoUrl,
        primaryColor: data.data.partner.primaryColor,
        theme: data.data.partner.theme,
        sessionExpiredRedirectUrl: data.data.partner.sessionExpiredRedirectUrl,
      },
      userData: data.data.userData,
      config: data.data.config,
      progress: data.data.progress,
      hasPhoneNumber:
        typeof data.data.hasPhoneNumber === "boolean"
          ? data.data.hasPhoneNumber
          : undefined,
    };
  } catch (error: unknown) {
    const { status, code, message } = getApiErrorInfo(error);

    if (status === 404) {
      throw new LinkClaimError(
        "Link not found",
        LINK_ERROR_CODE.LINK_NOT_FOUND,
        404
      );
    }

    if (status === 400) {
      throw new LinkClaimError(
        message || "Session not started",
        code || LINK_ERROR_CODE.SESSION_NOT_STARTED,
        400
      );
    }

    if (status === 410) {
      throw new LinkClaimError(
        "Link expired",
        LINK_ERROR_CODE.LINK_EXPIRED,
        410
      );
    }

    throw new LinkClaimError(
      "Failed to get session",
      LINK_ERROR_CODE.INTERNAL_ERROR,
      status
    );
  }
};

// ============================================
// Operations API (Exchange Rate, Quote, Cards)
// ============================================

/**
 * Exchange rate response
 */
export interface IExchangeRateResponse {
  sourceCurrency: string;
  destinationCurrency: string;
  rate: number;
  exchangeRate?: number;
  fundingFee?: number;
  transactionFee?: number;
  taxFee?: number;
  timestamp: string;
}

/**
 * Get exchange rate between two currencies
 * GET /widget/operations/exchange-rate
 */
export const getExchangeRate = async (
  sourceCurrency: string,
  destinationCurrency: string
): Promise<IExchangeRateResponse> => {
  const { data } = await widgetApi.get(
    `/widget/operations/exchange-rate`,
    { params: { sourceCurrency, destinationCurrency } }
  );

  return data.data;
};

/**
 * Quote request parameters
 */
export interface IQuoteRequest {
  sourceCountry: string;
  destinationCountry: string;
  fundingMethod: string; // 'card' | 'bank_account' | 'cash'
  payoutMethod: string; // 'card' | 'bank_account'
  amount: number;
  currency: string;
  cardId?: string; // Card ID for card payments (will be signed in the quote)
}

/**
 * Quote response
 */
export interface IQuoteResponse {
  quoteId: string;
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  /** @deprecated Use fundingFee + transactionFee + taxFee breakdown instead */
  fee?: number;
  totalFee?: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingMethod: string;
  payoutMethod: string;
  expiresAt: string;
  signedQuote: string;
  cardId?: string;
}

/**
 * Get a quote for a money transfer
 * POST /widget/operations/quote
 */
export const getQuote = async (
  request: IQuoteRequest,
  signal?: AbortSignal
): Promise<IQuoteResponse> => {
  const { data } = await widgetApi.post(`/widget/operations/quote`, request, {
    signal,
  });

  return data.data;
};

/**
 * Stored card response
 */
export interface IStoredCard {
  cardId: string;
  last4: string;
  brand: string;
  expiryMonth: string;
  expiryYear: string;
  cardholderName?: string;
  isDefault: boolean;
}

/**
 * Add card request
 */
export interface IAddCardRequest {
  token: string; // VGS token
  last4: string;
  brand: string;
  expiryMonth: string;
  expiryYear: string;
  cardholderName?: string;
}

/**
 * Add a funding card for the user
 * POST /widget/links/:linkId/cards
 */
export const addCard = async (
  linkId: string,
  card: IAddCardRequest
): Promise<IStoredCard> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/cards`,
    card
  );

  return data.data;
};

/**
 * List all funding cards for the user
 * GET /widget/links/:linkId/cards
 */
export const listCards = async (linkId: string): Promise<IStoredCard[]> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/cards`
  );

  return data.data;
};

/**
 * Remove a funding card
 * DELETE /widget/links/:linkId/cards/:cardId
 */
export const removeCard = async (
  linkId: string,
  cardId: string
): Promise<void> => {
  await widgetApi.delete(`/widget/links/${linkId}/cards/${cardId}`);
};

// ============================================
// Operations Create API
// ============================================

/**
 * Create operation request
 */
export interface ICreateOperationRequest {
  signedQuote: string;
  fundingPaymentMethodId: string;
  payoutPaymentMethodId?: string;
  notes?: string;
}

/**
 * Operation response
 */
export interface IOperationResponse {
  operationId: string;
  quoteId: string;
  status: string; // 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  /** @deprecated Use fundingFee + transactionFee + taxFee breakdown instead */
  fee?: number;
  fundingFee?: number;
  transactionFee?: number;
  taxFee?: number;
  totalFee?: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingPaymentMethodId: string;
  payoutPaymentMethodId?: string;
  notes?: string;
  isCancellationRequested?: boolean;
  cancelRequestedAt?: string;
  cancelRequestReason?: string;
  createdAt: string;
}

/**
 * Create a money transfer operation
 * POST /widget/links/:linkId/operations
 */
export const createOperation = async (
  linkId: string,
  request: ICreateOperationRequest
): Promise<IOperationResponse> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/operations`,
    request
  );

  return data.data;
};

/**
 * List all operations for the user
 * GET /widget/links/:linkId/operations
 */
export const listOperations = async (
  linkId: string
): Promise<IOperationResponse[]> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/operations`
  );

  return data.data;
};

/**
 * Get a single operation by ID
 * GET /widget/links/:linkId/operations/:operationId
 */
export const getOperation = async (
  linkId: string,
  operationId: string
): Promise<IOperationResponse> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/operations/${operationId}`
  );

  return data.data;
};

/**
 * Cancel operation response (soft-cancel)
 */
export interface ICancelOperationResponse {
  operationId: string;
  isCancellationRequested: boolean;
  cancelRequestReason: string;
}

/**
 * Cancel a money transfer operation
 * POST /widget/links/:linkId/operations/:operationId/cancel
 */
export const cancelOperation = async (
  linkId: string,
  operationId: string
): Promise<ICancelOperationResponse> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/operations/${operationId}/cancel`
  );

  return data.data;
};

// ============================================
// Payment Methods API (new system with VGS proxy tokenization)
// ============================================

import type {
  ICardPaymentMethod,
  IAllPaymentMethods,
  IAddCardPaymentMethodRequest,
  IVgsTokenizationRequest,
  IVgsTokenizationResponse,
} from "../interfaces/payment-method";

/**
 * Tokenize card data via VGS forward proxy
 * POST https://{vaultId}.{env}.verygoodproxy.com/post
 */
export const tokenizeCard = async (
  cardData: IVgsTokenizationRequest
): Promise<IVgsTokenizationResponse> => {
  const vgsProxyUrl = `https://${config.VGS_PROXY_VAULT_ID}.${config.VGS_ENV}.verygoodproxy.com/post`;

  const { data } = await axios.post(vgsProxyUrl, cardData, {
    headers: { "Content-Type": "application/json" },
  });

  return data;
};

/**
 * Register a tokenized card as a payment method
 * POST /widget/links/:linkId/payment-methods/cards
 */
export const addCardPaymentMethod = async (
  linkId: string,
  card: IAddCardPaymentMethodRequest
): Promise<ICardPaymentMethod> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/payment-methods/cards`,
    card
  );
  return data.data;
};

/**
 * Remove a card payment method
 * DELETE /widget/links/:linkId/payment-methods/cards/:cardId
 */
export const removeCardPaymentMethod = async (
  linkId: string,
  cardId: string
): Promise<void> => {
  await widgetApi.delete(
    `/widget/links/${linkId}/payment-methods/cards/${cardId}`
  );
};

/**
 * Enable or disable a card payment method
 * PATCH /widget/links/:linkId/payment-methods/cards/:cardId
 */
export const updateCardPaymentMethod = async (
  linkId: string,
  cardId: string,
  enabled: boolean
): Promise<ICardPaymentMethod> => {
  const { data } = await widgetApi.patch(
    `/widget/links/${linkId}/payment-methods/cards/${cardId}`,
    { enabled }
  );
  return data.data;
};

/**
 * Get all payment methods for the user (cards, bank accounts, etc.)
 * GET /widget/links/:linkId/payment-methods
 */
export const getAllPaymentMethods = async (
  linkId: string
): Promise<IAllPaymentMethods> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/payment-methods`
  );
  return data.data;
};

// ============================================
// CIP Readiness API
// ============================================

/**
 * CIP readiness response
 */
export interface ICipReadinessResponse {
  shouldRunCip: boolean;
  reason: string;
  cipTicketUrl: string | null;
  cipTicketId: string | null;
  missingFields?: string[];
}

/**
 * Check if the user needs to complete CIP verification before transferring
 * GET /widget/links/:linkId/cip-readiness
 */
export const checkCipReadiness = async (
  linkId: string
): Promise<ICipReadinessResponse> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/cip-readiness`
  );

  return data.data;
};

export interface ILegalDocumentResponse {
  doc: string;
  lang: string;
  html: string;
  fetchedAt: string;
  fromCache: boolean;
}

/**
 * Fetch a legal document HTML via the backend proxy.
 * No x-link-id required — public endpoint.
 * GET /widget/legal/:doc/:lang
 */
export const getLegalDocument = async (
  doc: string,
  lang: string
): Promise<ILegalDocumentResponse> => {
  const { data } = await widgetApi.get(`/widget/legal/${doc}/${lang}`);
  return data.data;
};
